/**
 * THE DOWNTOWN QUADRANT. Owned by `world`.
 *
 * Phase 1 of the city expansion grew the grid south and west; this is what makes the new
 * south-west corner read as downtown Montréal rather than as more Plateau. Two ideas:
 *
 *   1. ZONING IS A GRADIENT, NOT A LINE. `downtownScore()` is a pure function of world
 *      position — one minus the normalised distance from the outside south-west corner of the
 *      grid. Towers where the score is high, taller-than-Plateau midrise in the transition
 *      band, untouched triplexes everywhere else. A hard boundary between a 34-storey tower
 *      and a two-storey triplex is the thing that would make this read as two games stapled
 *      together; the real city has Concordia-scale midrise doing exactly this job between
 *      downtown and the Plateau.
 *
 *      The anchor and radii are derived from the graph, so a later phase that grows the grid
 *      again moves the zone with it instead of leaving downtown stranded inland.
 *
 *      ⚠ `fadeR` is chosen so the OLD CORE SCORES EXACTLY ZERO. The nearest point of the
 *      pre-expansion city to the anchor is 294 m away; fade ends at 285. Every graded shot
 *      lives in there and must not sprout a tower.
 *
 *   2. A TOWER BLOCK IS CHEAPER THAN A PLATEAU BLOCK. Downtown blocks are not subdivided into
 *      ~20 lots with staircases, balconies, stoops and window surrounds. They get one or two
 *      big podium lots, and a podium plus a shaft is about 90 triangles. That is why more than
 *      doubling the city's block count does not double its cost.
 *
 * MATERIALS (§4.4/N10). The curtain wall, spandrel and podium are `look`'s: this file only
 * ever asks for 'building.tower.glass' / '.spandrel' / '.podium' by key, through
 * `ctx.materials.get()`, and `towerMaterials()` falls back to the general building set if they
 * have not landed yet. Storey banding, the night window grid and the wet-glass response are
 * all baked into those materials, which is why the geometry here can afford to be a handful of
 * prisms — the shaft does not carry one box per floor.
 */

import { FACE } from './geom.js';

/** Rectangle ring in the [x,z,…] winding `MeshBuilder.prism` expects (CCW in XZ). */
function rect(minX, minZ, maxX, maxZ) {
  return [minX, minZ, maxX, minZ, maxX, maxZ, minX, maxZ];
}

function aabbOf(poly) {
  let minX = Infinity, minZ = Infinity, maxX = -Infinity, maxZ = -Infinity;
  for (let i = 0; i < poly.length; i += 2) {
    if (poly[i] < minX) minX = poly[i];
    if (poly[i] > maxX) maxX = poly[i];
    if (poly[i + 1] < minZ) minZ = poly[i + 1];
    if (poly[i + 1] > maxZ) maxZ = poly[i + 1];
  }
  return { minX, minZ, maxX, maxZ };
}

/**
 * The anchor the zone radiates from: the outside south-west corner of the street grid.
 * Derived, not hardcoded, so a later phase moves downtown with the grid.
 */
export function downtownAnchor(T, graph) {
  /**
   * ⚠ PINNED, AND THAT IS A CORRECTION.
   *
   * This used to return the grid's own south-west corner so downtown would "move with the
   * grid" in a later phase. Phase 3 proved that wrong: the westward extension is a MOUNTAIN,
   * not more city, so the derived anchor slid 220 m west and took downtown with it — measured,
   * six tower blocks appeared on Mont Royal's south flank and the real downtown lost them.
   *
   * The anchor is the phase-1 corridor corner, held as explicit coordinates. Downtown is a
   * PLACE, not a corner of whatever the grid happens to be this month.
   */
  void graph;
  return { x: T.downtown.anchorX, z: T.downtown.anchorZ };
}

/**
 * 0 = pure Plateau, 1 = the heart of downtown. Pure function of position — no rng, so the
 * zoning is identical on every machine and every reset (N3).
 */
export function downtownScore(T, graph, x, z) {
  const D = T.downtown;
  const a = downtownAnchor(T, graph);
  const dx = x - a.x, dz = z - a.z;
  const d = Math.sqrt(dx * dx + dz * dz);
  if (d <= D.coreR) return 1;
  if (d >= D.fadeR) return 0;
  return (D.fadeR - d) / (D.fadeR - D.coreR);
}

/**
 * Material set for the tower families, with the §4.4 fallback the lead asked for: if `look`
 * has not registered the curtain-wall keys yet, fall back to the general building set so the
 * quadrant builds as plain glass-and-stone boxes instead of `MaterialLibrary`'s debug magenta.
 */
export function towerMaterials(M, T) {
  /**
   * ⚠ `opts` APPLIES TO THE FALLBACK ONLY.
   *
   * This used to pass the fallback tint through on BOTH branches, so when `look`'s real
   * `building.tower.spandrel` existed it was still handed `{tint: towerSpandrelFallback}` and
   * came back as `building.tower.spandrel|t4c5460` — world silently overriding the material
   * owner's authored albedo with a stand-in colour meant for the case where their material was
   * missing. Caught by dumping `material.name` off the live meshes.
   */
  const pick = (key, fallback, fallbackOpts) =>
    (M.has && M.has(key) ? M.get(key) : M.get(fallback, fallbackOpts));
  /**
   * Two-step fallback: the lit-density variant, else the base curtain wall, else the general
   * building glass. Requested BY KEY — never `get(key, {emissiveIntensity})`, which nulls
   * `look`'s night scaling and pins the tower lit at noon (§4.9e).
   */
  const glassVariant = (suffix) => {
    const key = 'building.tower.glass' + suffix;
    if (M.has && M.has(key)) return M.get(key);
    return pick('building.tower.glass', 'building.glass');
  };
  return {
    Bright: glassVariant('.bright'),
    Dim: glassVariant('.dim'),
    Dark: glassVariant('.dark'),
    spandrel: pick('building.tower.spandrel', 'building.stone', { tint: T.tints.towerSpandrelFallback }),
    podium: pick('building.tower.podium', 'building.stone', { tint: T.tints.towerPodiumFallback }),
    /** True when the real set landed — reported through harnessStats so a miss is visible. */
    real: !!(M.has && M.has('building.tower.glass')),
  };
}

/**
 * Replace a downtown block's Plateau subdivision with one or two big podium lots.
 * Returns null when the block is not downtown, so the caller keeps its normal path.
 *
 * @param {number} score  downtownScore at the block centre
 * @param {Rng} rng       the block's own position-keyed fork (see layout-key.js)
 */
export function towerLots(T, block, score, rng) {
  const D = T.downtown;
  if (score < D.towerThreshold) return null;
  /**
   * ⚠ DOWNTOWN IS ON FLAT GROUND. Phase 3's westward extension put new blocks inside the
   * zone radius that happen to sit on Mont Royal's southern flank — measured, the tower count
   * went 6 → 15 and the new ones were on a grade. A podium is one level slab tens of metres
   * across; on a slope it either floats or buries, and neither is a skyline. Real downtown
   * Montréal stops where the hill starts, and so does this.
   */
  if ((block.mountain || 0) > D.maxMountain) return null;

  const x0 = block.x0 + D.podiumSetback, x1 = block.x1 - D.podiumSetback;
  const z0 = block.z0 + D.podiumSetback, z1 = block.z1 - D.podiumSetback;
  const w = x1 - x0, d = z1 - z0;
  if (w < D.lotMin || d < D.lotMin) return null;

  // Split along the longer axis when there is room for two real parcels. One podium filling a
  // whole 55 m block is a superblock; two is the ordinary downtown pattern.
  /**
   * ⚠ A RUELLE RUNS THROUGH SOME OF THESE BLOCKS AND A PODIUM WOULD PAVE OVER IT.
   *
   * `Ruelle Saint-Antoine` sits at x −285, mid-block between rue Guy and rue Peel — which is
   * inside a downtown block, because that is what a mid-block alley is. A single podium
   * covering the block would have been built straight over a driveable road: the truck would
   * have entered the alley and hit a wall that classify() still reports as carriageway.
   *
   * So an alley FORCES the two-parcel split, on the alley's own axis, with each parcel held
   * back by half the alley plus a service margin. Downtown gets a real loading lane between
   * two podium backs, which is exactly what the back of a tower block looks like.
   */
  const alley = block.ruNs || block.ruEw;
  const alleyAxis = block.ruNs ? 'x' : (block.ruEw ? 'z' : null);
  const along = alleyAxis || (w >= d ? 'x' : 'z');
  const lo = along === 'x' ? x0 : z0, hi = along === 'x' ? x1 : z1;
  const span = hi - lo;

  let cuts;
  if (alley) {
    const margin = alley.half + D.alleyMargin;
    const a = alley.pos;
    // Both sides must still be a real parcel, or the alley is too close to an edge to split on
    // and the block is left as Plateau rather than half-built.
    if (a - margin - lo < D.lotMin || hi - (a + margin) < D.lotMin) return null;
    cuts = [[lo, a - margin], [a + margin, hi]];
  } else if (span >= D.splitMin && rng.chance(D.splitP)) {
    const c = lo + span * rng.range(0.42, 0.58);
    const pad = D.parcelGap * 0.5;
    cuts = [[lo, c - pad], [c + pad, hi]];
  } else {
    cuts = [[lo, hi]];
  }

  const out = [];
  for (const [a0, a1] of cuts) {
    if (a1 - a0 < D.lotMin) continue;
    const minX = along === 'x' ? a0 : x0, maxX = along === 'x' ? a1 : x1;
    const minZ = along === 'x' ? z0 : a0, maxZ = along === 'x' ? z1 : a1;
    out.push({
      poly: rect(minX, minZ, maxX, maxZ),
      cx: (minX + maxX) * 0.5, cz: (minZ + maxZ) * 0.5,
      w: maxX - minX, d: maxZ - minZ,
      tower: true,
    });
  }
  return out.length ? out : null;
}

/**
 * Build one podium-and-shaft tower.
 *
 * TRIANGLE BUDGET. Everything here is prisms and boxes: podium + cap, an optional cornice, the
 * shaft, up to `mullions` vertical fins, one spandrel band at the setback, the setback shaft
 * and a crown. Measured at 90-130 triangles for a whole tower, against roughly 1,600 for a
 * triplex lot with its staircase and window reveals. The storey rhythm is the curtain-wall
 * MAP's job, not geometry's — one box per floor over 34 floors would be 400 triangles a tower
 * for a pattern the material already draws (§9).
 *
 * @param {object} mbs   the lot's SECTOR builder bag; uses towerGlass / towerSpandrel /
 *                       towerPodium, which are separate from the storefront `glass` family
 * @param {Array} shells push {minX,minZ,maxX,maxZ,y0,y1} per shaft for the far skyline shell
 * @param {Array} colliders  static collision, same array world._build() feeds StaticCollision
 * @returns {{h:number, storeys:number}}
 */
export function buildTower(T, graph, lot, score, rng, mbs, shells, colliders) {
  const D = T.downtown;
  const b = aabbOf(lot.poly);
  /**
   * Podium base from the LOWEST corner, not the centre — same rule as LotBuilder's `baseDrop`.
   * `maxMountain` keeps tower parcels off the real grades, but the city's own swell still runs
   * under a 50 m podium and a single centre sample would leave a corner in the air.
   */
  const gy = graph.walkY(lot.cx, lot.cz);
  let gyMin = Infinity;
  for (let i = 0; i < lot.poly.length; i += 2) {
    const cy = graph.walkY(lot.poly[i], lot.poly[i + 1]);
    if (cy < gyMin) gyMin = cy;
  }
  const podBury = Math.max(2.2, gy - gyMin + 2.2);

  // ---- podium ------------------------------------------------------------
  // Two or three storeys of retail/parking that meets the pavement. This is the only part of
  // a tower the player ever sees up close, so it is the part that gets the real material.
  const podStoreys = D.podiumStoreys[rng.int(0, D.podiumStoreys.length)];
  const podH = podStoreys * D.podiumStoreyH;
  const podTop = gy + podH;
  const pod = rect(b.minX, b.minZ, b.maxX, b.maxZ);
  // `-2.2` matches the triplex path: buildings start below grade so the terrain swell running
  // under a flat footprint never opens a gap at the base (§4.9c is about the TOP of the ground,
  // this is the bottom).
  mbs.towerPodium.prism(pod, gy - podBury, podTop, false, D.podiumUv);
  /**
   * GLAZED BASE. The podium is the only part of a tower at the player's eye height for the
   * whole time they are downtown, and a blank 10 m concrete box either side of the street is
   * the single most boring thing this quadrant could offer. A recessed glazing band on the
   * curtain-wall material reads as lobby and retail, picks up the same night emissive as the
   * shaft, and costs eight triangles.
   */
  const gi = D.baseGlassInset;
  // Podium glazing goes on the DIM variant regardless of the shaft's: a lobby is lit at night
  // but it is not a stack of occupied floors, and it is the part nearest the player.
  mbs.towerGlassDim.prism(rect(b.minX + gi, b.minZ + gi, b.maxX - gi, b.maxZ - gi),
    gy + D.baseGlassY0, gy + D.baseGlassY1, false, D.baseGlassUv);
  // Cornice band at the podium head — the line that separates base from shaft at street level.
  const corn = rect(b.minX - D.corniceOut, b.minZ - D.corniceOut, b.maxX + D.corniceOut, b.maxZ + D.corniceOut);
  mbs.towerSpandrel.prism(corn, podTop - D.corniceH, podTop, false, 0.4, true);

  // ---- shaft -------------------------------------------------------------
  const storeys = Math.max(D.minStoreys, Math.round(
    D.minStoreys + (D.maxStoreys - D.minStoreys) * score * rng.range(D.heightJitter, 1)));
  const shaftH = storeys * D.storeyH;

  // The shaft is set back from the podium on all sides and is deliberately not centred: an
  // off-centre core is what stops a row of towers reading as one repeated object.
  const insetX = Math.min(D.shaftInsetMax, (b.maxX - b.minX) * D.shaftInsetFrac);
  const insetZ = Math.min(D.shaftInsetMax, (b.maxZ - b.minZ) * D.shaftInsetFrac);
  const offX = rng.spread(insetX * 0.45), offZ = rng.spread(insetZ * 0.45);
  let sx0 = b.minX + insetX + offX, sx1 = b.maxX - insetX + offX;
  let sz0 = b.minZ + insetZ + offZ, sz1 = b.maxZ - insetZ + offZ;
  // Slab towers: squeeze one axis so the mass has a broad face and a thin edge.
  if (rng.chance(D.slabP)) {
    if (rng.chance(0.5)) { const m = (sz0 + sz1) * 0.5, h = (sz1 - sz0) * D.slabFrac * 0.5; sz0 = m - h; sz1 = m + h; }
    else { const m = (sx0 + sx1) * 0.5, h = (sx1 - sx0) * D.slabFrac * 0.5; sx0 = m - h; sx1 = m + h; }
  }

  const setback = storeys >= D.setbackMinStoreys && rng.chance(D.setbackP);
  const breakY = podTop + shaftH * (setback ? D.setbackAt : 1);
  const topY = podTop + shaftH;

  /**
   * LIT DENSITY IS A MATERIAL VARIANT, NOT A UNIFORM WE SET.
   *
   * `look` registers 'building.tower.glass.bright' / '.dim' / '.dark' and requires them to be
   * chosen by KEY: `get(key, {emissiveIntensity})` nulls the night scaling and pins the tower
   * lit at noon (the documented `building.window` trap, §4.9e). They are uniform-only variants,
   * so they add no shader program — one merged mesh per variant per sector is the whole cost.
   *
   * Weighted so most towers are dark or dim and roughly one is bright: §10 requires the CONES
   * to stay the highest-contrast objects in frame, and a downtown of fully-lit curtain wall is
   * the one thing that could take that away at night.
   */
  /**
   * ⚠ THE TALLEST TOWER MUST NEVER BE THE DEAD ONE.
   *
   * '.dark' is emissiveIntensity 0 — `look` registers it as "shuttered / under construction",
   * and one of those in a skyline is characterful. The first weighting produced THREE of eleven
   * and one of them was the hero mass in the middle of frame, which reads as a missing-geometry
   * bug rather than an empty building — `look` filed it as exactly that. Weights are lower now,
   * and any tower at or above `litTallStoreys` re-picks from the lit variants only, so the
   * silhouette that carries the skyline always has windows in it.
   */
  let litKey = rng.pickWeighted(D.litVariants, D.litWeights);
  if (litKey === 'Dark' && storeys >= D.litTallStoreys) {
    litKey = rng.pickWeighted(D.litVariantsTall, D.litWeightsTall);
  }
  const glass = mbs['towerGlass' + litKey];
  glass.prism(rect(sx0, sz0, sx1, sz1), podTop - 0.4, breakY, false, D.glassUv);

  /**
   * ⚠ THE MULLION FINS ARE GONE, AND THEY WERE THE BLACK TOWER.
   *
   * They were meant to be vertical fins on the facade. They were written as boxes spanning the
   * FULL width of the shaft and its full height, thin on the other axis — a pair of crossing
   * internal slabs, not fins. `FACE.SIDES` then emitted their two broad faces at full width ×
   * full height, and for any tower where the slab landed in front of the glass plane from the
   * viewing side, that broad face WAS the facade: opaque dark spandrel covering the entire
   * curtain wall. That is the "central tower stays entirely black" defect `look` filed, and it
   * had nothing to do with the '.dark' lit variant I first suspected.
   *
   * Attributed by raycasting into the black pixels rather than by reading the code (§4.9b):
   * `world:sector3.towerSpandrel` at 73.4-79.0 m with `towerGlassDim` behind it at 85.7 m.
   *
   * They are not replaced, because they were never needed. `look`'s curtain-wall map draws the
   * vertical mullion as its DOMINANT line (materials/tuning towerMullionV) — this was geometry
   * duplicating a pattern the material already paints, which is the same mistake props.js
   * records for the cone bands. Removing them costs nothing visually and saves ~32 triangles a
   * tower on top of un-hiding the glazing.
   */

  let cx0 = sx0, cz0 = sz0, cx1 = sx1, cz1 = sz1;
  if (setback) {
    // Spandrel band at the shoulder, then a narrower shaft above it.
    const band = rect(sx0 - 0.25, sz0 - 0.25, sx1 + 0.25, sz1 + 0.25);
    mbs.towerSpandrel.prism(band, breakY - D.bandH, breakY, true, 0.4, true);
    const f = D.setbackIn;
    const mx = (sx0 + sx1) * 0.5, mz = (sz0 + sz1) * 0.5;
    cx0 = mx - (sx1 - sx0) * f * 0.5; cx1 = mx + (sx1 - sx0) * f * 0.5;
    cz0 = mz - (sz1 - sz0) * f * 0.5; cz1 = mz + (sz1 - sz0) * f * 0.5;
    glass.prism(rect(cx0, cz0, cx1, cz1), breakY - D.bandH, topY, false, D.glassUv);
  }

  // ---- crown -------------------------------------------------------------
  // Mechanical box, and sometimes a mast. Breaks the flat-top silhouette, which is the whole
  // job of a crown from 400 m away.
  const cmx = (cx0 + cx1) * 0.5, cmz = (cz0 + cz1) * 0.5;
  const cw = (cx1 - cx0) * D.crownFrac, cd = (cz1 - cz0) * D.crownFrac;
  mbs.towerSpandrel.prism(rect(cmx - cw * 0.5, cmz - cd * 0.5, cmx + cw * 0.5, cmz + cd * 0.5),
    topY - 0.2, topY + D.crownH, true, 0.3, true);
  let crest = topY + D.crownH;
  if (rng.chance(D.mastP)) {
    mbs.towerSpandrel.box(cmx, crest + D.mastH * 0.5, cmz, 0.7, D.mastH, 0.7, FACE.SIDES, 0.2);
    crest += D.mastH;
  }

  // ---- far shell + collision --------------------------------------------
  /**
   * ⚠ THIS SHELL ATE THE ENTIRE CURTAIN WALL. The inset is load-bearing — see
   * buildSkylineShells() for the measurement.
   *
   * Clamped to a quarter of each axis so a narrow slab tower still gets a shell instead of
   * being skipped for having a negative footprint.
   */
  const inX = Math.min(D.shellInset, (sx1 - sx0) * 0.25);
  const inZ = Math.min(D.shellInset, (sz1 - sz0) * 0.25);
  const inCX = Math.min(D.shellInset, (cx1 - cx0) * 0.25);
  const inCZ = Math.min(D.shellInset, (cz1 - cz0) * 0.25);
  shells.push({
    sector: lot.sector,
    minX: sx0 + inX, minZ: sz0 + inZ,
    maxX: sx1 - inX, maxZ: sz1 - inZ,
    // Held BELOW the detailed shaft's top as well as inside its faces. The shell used to run to
    // `topY` exactly, so its cap was coplanar with the glass cap and its silhouette stood proud
    // of the glass wherever the two disagreed.
    y0: gy, y1: topY - D.shellTopDrop, breakY: breakY - D.shellTopDrop, setback,
    cx0: cx0 + inCX, cz0: cz0 + inCZ,
    cx1: cx1 - inCX, cz1: cz1 - inCZ,
  });
  // The PODIUM is the collider, not the shaft: the podium is what meets the street, and it is
  // wider, so a shaft collider would let the truck drive under an overhang. `top` is generous
  // because §4.9c terrain sits near +1.4 and the truck must never mount a podium.
  colliders.push({
    minX: b.minX, minZ: b.minZ, maxX: b.maxX, maxZ: b.maxZ,
    y0: gy - 3, top: podTop + 2, kind: 'building',
  });

  return { h: crest - gy, storeys };
}

/**
 * The always-resident far skyline: one open prism per shaft, no caps, no crown.
 *
 * WHY IT EXISTS. Tower detail is emitted into the SECTOR builders, and sector groups are
 * distance-culled (`stream.sectorRadius`). A 130 m tower vanishing when the group switches off
 * is a hard pop on the horizon — the one silhouette in the frame you cannot miss. This mesh is
 * never culled and never streamed, exactly like the perimeter barrier, so the skyline is
 * continuous from anywhere in the city.
 *
 * ⚠ IT IS AN LOD SWAP, NOT AN INSET. THE INSET APPROACH FAILED TWICE.
 *
 * The first version inset each shell 0.22 m from its shaft and this comment claimed it
 * therefore "contributes no pixels". That claim was never measured, and it was wrong. 0.22 m is
 * inside depth-buffer precision at the 150-300 m the downtown skyline is actually viewed from,
 * so the DARK spandrel shell won the depth test across most of every facade. `look` falsified
 * it with the toggle protocol (§4.9b): hiding `world:skylineShell` took bright pixels from
 * 1,311 to 36,514, a factor of 28. Night-rain downtown was rendering as a near-black mass with
 * the entire lit curtain wall hidden behind a shell that was supposed to be invisible.
 *
 * Widening the inset to 1 m and dropping the top 1 m got the ratio from 28x to 2.66x and NO
 * FURTHER. Tagging the shell with a flat red material and re-rendering (the only way to
 * attribute a pixel, §4.9b) showed why: red survived along every silhouette edge, across the
 * crown band above the glass, and in a band above the podium. A shell that is "inside" a
 * hollow, open-topped tube of single-sided walls is not actually hidden — it shows anywhere
 * the tube has an opening or a grazing edge, and no inset value fixes that.
 *
 * So the two are now mutually exclusive, which is what `LodPair` does for every other repeated
 * thing in this city. One shell mesh PER SECTOR, parented to `this.ground` so it is never
 * distance-culled, and `update()` sets `shell.visible = !sector.group.visible`. Exactly one of
 * {detail, shell} draws for a given sector, so there is no depth interaction to get wrong and
 * no dependence on inset precision at any distance.
 *
 * The 1 m inset is KEPT anyway: it costs nothing and it means the swap at the cull boundary
 * shrinks the silhouette slightly rather than growing it, which is the less noticeable
 * direction for a pop.
 *
 * 10 triangles per shaft; the whole quadrant's shell is in the low hundreds.
 */
export function buildSkylineShells(T, shells, mbFor) {
  for (const s of shells) {
    const mb = mbFor(s.sector);
    if (!mb) continue;
    if (s.maxX - s.minX < 0.5 || s.maxZ - s.minZ < 0.5) continue;
    mb.prism(rect(s.minX, s.minZ, s.maxX, s.maxZ), s.y0, s.setback ? s.breakY : s.y1, true, 0.2, true);
    if (s.setback && s.cx1 - s.cx0 > 0.5 && s.cz1 - s.cz0 > 0.5) {
      mb.prism(rect(s.cx0, s.cz0, s.cx1, s.cz1), s.breakY - 0.2, s.y1, true, 0.2, true);
    }
  }
}
